/**
 * /admin/v1/knowledge/* API 客户端 · M09 Knowledge 管理（S3 Day13）
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

export type KnowledgeContentType = "markdown" | "pdf" | "word" | "text";
export type KnowledgeDocumentStatus = "draft" | "indexed" | "failed";

export interface KnowledgeCollectionStatus {
	purpose: "products" | "documents";
	collection_name: string;
	status: "not_configured" | "configured" | "empty" | "ok" | "error";
	points_count: number | null;
	vectors_count: number | null;
	local_document_count: number;
	message: string;
}

export interface KnowledgeIndexStatusResponse {
	merchant_id: string;
	qdrant_enabled: boolean;
	qdrant_probe: boolean;
	local_document_count: number;
	last_indexed_at: string | null;
	collections: KnowledgeCollectionStatus[];
}

export interface KnowledgeDocumentItem {
	doc_id: string;
	merchant_id: string;
	namespace: string;
	title: string;
	content_type: KnowledgeContentType;
	status: KnowledgeDocumentStatus;
	body_preview: string;
	chunk_count: number;
	indexed_at: string | null;
	updated_by: string | null;
	updated_at: string;
}

export interface KnowledgeDocumentListResponse {
	items: KnowledgeDocumentItem[];
	total: number;
}

export interface KnowledgeDocumentCreateRequest {
	merchant_id: string;
	namespace: string;
	title: string;
	content_type: KnowledgeContentType;
	body_text: string;
	updated_by?: string | null;
}

export interface KnowledgeDocumentDeleteResponse {
	doc_id: string;
	deleted: boolean;
}

export interface KnowledgeSearchSandboxRequest {
	merchant_id: string;
	query: string;
	namespaces: string[];
	top_k: number;
}

export interface KnowledgeSearchHit {
	namespace: string;
	doc_id: string;
	title: string;
	content: string;
	score: number;
}

export interface KnowledgeSearchSandboxResponse {
	merchant_id: string;
	source: "local_lexical";
	total: number;
	items: KnowledgeSearchHit[];
	audit_id: string;
}

export interface KnowledgeMetricBucket {
	hour_start: string;
	total_searches: number;
	hit_count: number;
	hit_rate: number;
	average_top_score: number;
}

export interface KnowledgePrefetchMetricsResponse {
	merchant_id: string;
	window_hours: number;
	latest_total_searches: number;
	latest_hit_rate: number;
	buckets: KnowledgeMetricBucket[];
}

export function useKnowledgeIndexStatus(merchantId: string, probeQdrant = true) {
	// 默认 probe_qdrant=true 真探活：不探活时后端恒返 "configured"（假绿灯，掩盖 collection 404）。
	return useQuery<KnowledgeIndexStatusResponse>({
		queryKey: ["admin", "knowledge", "index-status", merchantId, probeQdrant],
		queryFn: async () => {
			const { data } = await apiClient.get<KnowledgeIndexStatusResponse>(
				"/admin/v1/knowledge/index-status",
				{ params: { merchant_id: merchantId, probe_qdrant: probeQdrant } },
			);
			return data;
		},
		enabled: merchantId.length > 0,
	});
}

export function useKnowledgeDocuments(merchantId: string) {
	return useQuery<KnowledgeDocumentListResponse>({
		queryKey: ["admin", "knowledge", "documents", merchantId],
		queryFn: async () => {
			const { data } = await apiClient.get<KnowledgeDocumentListResponse>(
				"/admin/v1/knowledge/documents",
				{ params: { merchant_id: merchantId } },
			);
			return data;
		},
		enabled: merchantId.length > 0,
	});
}

export function useKnowledgePrefetchMetrics(merchantId: string) {
	return useQuery<KnowledgePrefetchMetricsResponse>({
		queryKey: ["admin", "knowledge", "metrics", merchantId],
		queryFn: async () => {
			const { data } = await apiClient.get<KnowledgePrefetchMetricsResponse>(
				"/admin/v1/knowledge/prefetch-metrics",
				{ params: { merchant_id: merchantId, window_hours: 6 } },
			);
			return data;
		},
		enabled: merchantId.length > 0,
	});
}

export function useCreateKnowledgeDocument() {
	const queryClient = useQueryClient();
	return useMutation<KnowledgeDocumentItem, unknown, KnowledgeDocumentCreateRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<KnowledgeDocumentItem>(
				"/admin/v1/knowledge/documents",
				body,
			);
			return data;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "knowledge", "documents", data.merchant_id],
			});
			queryClient.invalidateQueries({
				queryKey: ["admin", "knowledge", "index-status", data.merchant_id],
			});
		},
	});
}

export function useDeleteKnowledgeDocument(merchantId: string) {
	const queryClient = useQueryClient();
	return useMutation<KnowledgeDocumentDeleteResponse, unknown, string>({
		mutationFn: async (docId) => {
			const { data } = await apiClient.delete<KnowledgeDocumentDeleteResponse>(
				`/admin/v1/knowledge/documents/${encodeURIComponent(docId)}`,
				{ params: { merchant_id: merchantId } },
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "knowledge", "documents", merchantId] });
			queryClient.invalidateQueries({
				queryKey: ["admin", "knowledge", "index-status", merchantId],
			});
		},
	});
}

export function useKnowledgeSearchSandbox(merchantId: string) {
	const queryClient = useQueryClient();
	return useMutation<KnowledgeSearchSandboxResponse, unknown, KnowledgeSearchSandboxRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<KnowledgeSearchSandboxResponse>(
				"/admin/v1/knowledge/search-sandbox",
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "knowledge", "metrics", merchantId] });
		},
	});
}
